import React, { useEffect, useMemo, useState } from "react";
import { Box, Dialog, IconButton, Skeleton, Typography } from "@mui/material";
import { apiFetch } from "@/auth/token";
import { makeBearerFetch, useEmbedApiAuth } from "@/api/embedApiContext";
import type { Citation } from "../../../chat/types";
import { AnimatedCard } from "../../Common/AnimatedCard";

// Inline SVG, as MediaImageCard does: @mui/icons-material is not a dependency.
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden="true">
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const THUMB_EDGE = 512;
const FULL_EDGE = 2048;

type Media = NonNullable<Citation["media"]>;

/** The access-checked image endpoint (spec §12). A tile/region citation asks
 *  for its crop; a picture inside a PDF/DOCX/PPTX asks for its locator (PDF
 *  page + box, or the package part) instead — the endpoint rejects `crop`
 *  together with a locator. The server scales the result to fit `max_edge`. */
export function kbImageUrl(media: Media, maxEdge: number): string {
  const params = new URLSearchParams();
  const locator = media.locator;
  if (locator && "part" in locator) {
    params.set("part", locator.part);
  } else if (locator) {
    params.set("page", String(locator.page));
    params.set("box", locator.box.join(","));
  } else if (media.crop) {
    params.set("crop", media.crop.join(","));
  }
  params.set("max_edge", String(maxEdge));
  return `/core/v1/documents/${encodeURIComponent(String(media.document_id))}/image?${params}`;
}

// A 503 means the server's image decoders are busy, not that the image is
// gone: retry a couple of times, after the server's Retry-After (whole
// seconds; an HTTP date or garbage falls back to the default), capped so a
// card never waits long, then hide the card as for any other failure.
const BUSY_RETRIES = 2;
const DEFAULT_RETRY_MS = 2_000;
const MAX_RETRY_MS = 10_000;

function retryDelayMs(retryAfter: string | null): number {
  const text = retryAfter?.trim() ?? "";
  const seconds = Number(text);
  if (text === "" || !Number.isFinite(seconds) || seconds < 0) return DEFAULT_RETRY_MS;
  return Math.min(seconds * 1000, MAX_RETRY_MS);
}

/** Resolves after `ms`, or rejects with an AbortError once `signal` aborts
 *  (the card unmounted or its url changed), so no retry fires late. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException("aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    if (signal.aborted) abort();
    else signal.addEventListener("abort", abort, { once: true });
  });
}

type Loaded = { state: "loading" } | { state: "ready"; src: string } | { state: "failed" };

type ImageFetch = (path: string, signal: AbortSignal) => Promise<Response>;

/** Fetches an API path the way useAuthedApi's clients do. In the embed the
 *  page belongs to the HOST app, so a relative URL would reach the host (whose
 *  SPA answers 200 with its HTML page): ask the embed's API origin with its
 *  Bearer token instead. In the SPA, the cookie against VITE_API_URL. Never
 *  redirects to login: a background image must not navigate the page away. */
function useImageFetch(): ImageFetch {
  const embed = useEmbedApiAuth();
  return useMemo<ImageFetch>(() => {
    if (embed) {
      const bearerFetch = makeBearerFetch(embed.getToken);
      const base = embed.apiUrl.replace(/\/+$/, "");
      return (path, signal) => bearerFetch(`${base}${path}`, { signal });
    }
    const base = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");
    return (path, signal) => apiFetch(`${base}${path}`, { signal }, { redirectOnUnauthorized: false });
  }, [embed]);
}

/** Start loading only once the card is within ~200px of the viewport, so a
 *  long history doesn't fetch every cited image at once. Without
 *  IntersectionObserver (old browsers, jsdom) it loads immediately. */
function useNearViewport(): [React.RefCallback<HTMLElement>, boolean] {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (near || !node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [near, node]);
  return [setNode, near];
}

/** Fetches an image over the session cookie (a plain <img src> would bypass
 *  apiFetch) and exposes it as an object URL, revoked when the url changes or
 *  the card unmounts. Never persisted, so a reloaded history re-fetches and
 *  re-checks access. Fails safe: any non-OK status or network error is
 *  "failed" and the card hides — a missing picture beats a broken-image icon.
 *  A 401 does NOT bounce to login: a background image must not navigate the
 *  page away mid-conversation. A 503 (the server's decode slots are busy) is
 *  retried a couple of times after its Retry-After; every other failure is
 *  final. `null` means "don't load yet". */
function useAuthedImage(url: string | null): Loaded {
  const fetchImage = useImageFetch();
  // Keyed by url so a changed url reads as loading on its first render,
  // not as the previous image.
  const [loaded, setLoaded] = useState<{ url: string; result: Loaded } | null>(null);
  useEffect(() => {
    if (!url) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    const controller = new AbortController();
    const settle = (result: Loaded) => {
      if (!cancelled) setLoaded({ url, result });
    };
    (async () => {
      try {
        let resp = await fetchImage(url, controller.signal);
        for (let retry = 0; resp.status === 503 && retry < BUSY_RETRIES; retry++) {
          await sleep(retryDelayMs(resp.headers.get("Retry-After")), controller.signal);
          if (cancelled) return;
          resp = await fetchImage(url, controller.signal);
        }
        if (!resp.ok) {
          // 401/403/404 are expected (expired session, lost access, deleted
          // document); anything else is worth a trace in the console.
          if (![401, 403, 404].includes(resp.status)) {
            console.warn("kb image fetch failed", { url, status: resp.status });
          }
          settle({ state: "failed" });
          return;
        }
        // A 200 that is not an image (a proxy or host SPA's HTML fallback)
        // would render as a broken-image icon: treat it as any other failure.
        const type = resp.headers.get("Content-Type") ?? "";
        if (!type.toLowerCase().startsWith("image/")) {
          console.warn("kb image fetch returned a non-image", { url, type });
          settle({ state: "failed" });
          return;
        }
        const blob = await resp.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        settle({ state: "ready", src: objectUrl });
      } catch (err) {
        if (cancelled) return;
        console.warn("kb image fetch failed", { url, err });
        settle({ state: "failed" });
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, fetchImage]);
  return loaded && loaded.url === url ? loaded.result : { state: "loading" };
}

interface Props {
  citation: Citation;
}

/** A cited knowledge image under an answer: a thumbnail with the source title
 *  that opens a zoomable full view. Renders nothing when the image can't be
 *  loaded (no access, gone, or an error). */
export function KbImageCard({ citation }: Props) {
  const media = citation.media;
  const title = citation.title || "Knowledge image";
  const [viewportRef, nearViewport] = useNearViewport();
  const thumb = useAuthedImage(media && nearViewport ? kbImageUrl(media, THUMB_EDGE) : null);
  const [open, setOpen] = useState(false);

  if (!media || thumb.state === "failed") return null;

  return (
    <AnimatedCard variant="sm" sx={{ p: 1, width: 200, flexShrink: 0 }}>
      <Box ref={viewportRef}>
        {thumb.state === "loading" ? (
          <Skeleton variant="rounded" width="100%" height={140} />
        ) : (
          <Box
            component="button"
            type="button"
            data-ph="kb_image-open"
            aria-label={`Open image: ${title}`}
            onClick={() => setOpen(true)}
            sx={{
              display: "block",
              width: "100%",
              height: 140,
              p: 0,
              border: 0,
              borderRadius: 1.5,
              overflow: "hidden",
              cursor: "zoom-in",
              bgcolor: "action.hover",
            }}
          >
            <img
              src={thumb.src}
              alt={title}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" noWrap title={title} sx={{ display: "block", mt: 0.5 }}>
          {title}
        </Typography>
      </Box>
      {open && <KbImageFullView media={media} title={title} onClose={() => setOpen(false)} />}
    </AnimatedCard>
  );
}

/** The full-size view. Mounted only while open so the large image is fetched
 *  on demand and its object URL is released on close. Click toggles between
 *  fit-to-screen and actual size (scrollable). Escape closes (MUI Dialog). */
function KbImageFullView({ media, title, onClose }: { media: Media; title: string; onClose: () => void }) {
  const full = useAuthedImage(kbImageUrl(media, FULL_EDGE));
  const [actualSize, setActualSize] = useState(false);

  return (
    <Dialog open onClose={onClose} maxWidth={false} aria-label={title}>
      <Box sx={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh", overflow: "auto" }}>
        <IconButton
          aria-label="Close image"
          data-ph="kb_image-close"
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, bgcolor: "background.paper" }}
        >
          <CloseIcon />
        </IconButton>
        {full.state === "loading" && <Skeleton variant="rectangular" width={480} height={360} />}
        {full.state === "failed" && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 4 }}>
            This image is no longer available.
          </Typography>
        )}
        {full.state === "ready" && (
          <Box
            component="button"
            type="button"
            data-ph="kb_image-toggle"
            aria-label={actualSize ? "Fit image to screen" : "Show image at actual size"}
            aria-pressed={actualSize}
            onClick={() => setActualSize((v) => !v)}
            sx={{ display: "block", p: 0, border: 0, background: "transparent", cursor: actualSize ? "zoom-out" : "zoom-in" }}
          >
            <img
              src={full.src}
              alt={`${title} (full size)`}
              style={
                actualSize
                  ? { display: "block" }
                  : { display: "block", maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }
              }
            />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
